import { type ReactNode } from 'react';

/* ─── Chip Group ────────────────────────────────────────────── */

interface ChipGroupProps<T extends string> {
    options: { value: T; label: string; icon?: ReactNode }[];
    selected: T | T[];
    onChange: (value: T | T[]) => void;
    multi?: boolean;
    maxSelect?: number;
    columns?: 2 | 3;
}

export function ChipGroup<T extends string>({
    options,
    selected,
    onChange,
    multi = false,
    maxSelect = 3,
    columns = 2,
}: ChipGroupProps<T>) {
    const selectedArr = Array.isArray(selected) ? selected : [selected];

    const toggle = (value: T) => {
        if (multi) {
            const arr = selectedArr.includes(value)
                ? selectedArr.filter((v) => v !== value)
                : selectedArr.length < maxSelect
                    ? [...selectedArr, value]
                    : selectedArr;
            onChange(arr as T[]);
        } else {
            onChange(value);
        }
    };

    return (
        <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
            {options.map((opt) => {
                const active = selectedArr.includes(opt.value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggle(opt.value)}
                        className={`
                            relative flex items-center justify-center gap-2 
                            px-4 py-3.5 rounded-2xl text-sm font-semibold
                            transition-all duration-200 select-none
                            ${active
                                ? 'bg-gold-500/20 text-gold-400 ring-2 ring-gold-500/60 shadow-[0_0_16px_rgba(212,175,55,0.15)]'
                                : 'bg-surface-card text-text-secondary ring-1 ring-white/[0.06] hover:ring-white/[0.12] hover:bg-surface-card/80'
                            }
                        `}
                    >
                        {opt.icon && <span className="text-lg">{opt.icon}</span>}
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}
